import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentsIcon from '@mui/icons-material/Payments';
import TuneIcon from '@mui/icons-material/Tune';
import DownloadIcon from '@mui/icons-material/Download';
import axiosInstance from '../../lib/axios';
import RecordPaymentModal from './components/RecordPaymentModal';
import SetCreditLimitModal from './components/SetCreditLimitModal';
import { palette } from '../../theme/colors';
import {
  CreditAccountSummary,
  CreditAccountStatus,
  CreditTransactionType,
} from '../../types/credit';

const fetchCreditAccount = async (id: string): Promise<CreditAccountSummary> => {
  const response = await axiosInstance.get<CreditAccountSummary>(`/credit/accounts/${id}`);
  return response.data;
};

const formatCurrency = (amount: number | null | undefined) => {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: 'DZD',
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getCustomerLabel = (
  customer?: { firstName?: string; lastName?: string; email?: string } | null
) => {
  if (!customer) return '—';
  const name = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
  return name || customer.email || '—';
};

const STATUS_LABEL_KEYS: Record<CreditAccountStatus, string> = {
  [CreditAccountStatus.ACTIVE]: 'credit.status.active',
  [CreditAccountStatus.SUSPENDED]: 'credit.status.suspended',
  [CreditAccountStatus.CLOSED]: 'credit.status.closed',
};

const STATUS_COLORS: Record<CreditAccountStatus, 'success' | 'warning' | 'default'> = {
  [CreditAccountStatus.ACTIVE]: 'success',
  [CreditAccountStatus.SUSPENDED]: 'warning',
  [CreditAccountStatus.CLOSED]: 'default',
};

const TRANSACTION_LABEL_KEYS: Record<CreditTransactionType, string> = {
  [CreditTransactionType.CHARGE]: 'credit.transactionType.charge',
  [CreditTransactionType.PAYMENT]: 'credit.transactionType.payment',
  [CreditTransactionType.ADJUSTMENT]: 'credit.transactionType.adjustment',
};

// CHARGE grows the debt, PAYMENT clears it, ADJUSTMENT is a manual correction either way.
const TRANSACTION_COLORS: Record<CreditTransactionType, 'error' | 'success' | 'info'> = {
  [CreditTransactionType.CHARGE]: 'error',
  [CreditTransactionType.PAYMENT]: 'success',
  [CreditTransactionType.ADJUSTMENT]: 'info',
};

export const CreditShow = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Each flag drives one dialog mounted at the bottom of this page; the header buttons toggle them.
  const [isRecordPaymentOpen, setIsRecordPaymentOpen] = useState(false);
  const [isSetCreditLimitOpen, setIsSetCreditLimitOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);

  const {
    data: account,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['credit-account', id],
    queryFn: () => fetchCreditAccount(id!),
    enabled: !!id,
  });

  const getStatusLabel = (status: CreditAccountStatus) => {
    const key = STATUS_LABEL_KEYS[status];
    return key ? t(key) : '—';
  };

  const getTransactionLabel = (type: CreditTransactionType) => {
    const key = TRANSACTION_LABEL_KEYS[type];
    return key ? t(key) : '—';
  };

  const handleDownloadStatement = async () => {
    if (!id) return;
    setIsDownloading(true);
    setDownloadFailed(false);
    let objectUrl: string | null = null;
    try {
      const response = await axiosInstance.get(`/credit/accounts/${id}/statement.pdf`, {
        responseType: 'blob',
      });
      objectUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `credit-statement-${id}.pdf`;
      link.click();
    } catch {
      setDownloadFailed(true);
    } finally {
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/app/credit')}>
            <ArrowBackIcon />
          </IconButton>
          <Skeleton variant="text" width={240} height={40} />
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={140} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={140} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={140} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={320} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error || !account) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/app/credit')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">{t('credit.show.notFound')}</Typography>
        </Box>
        <Card>
          <CardContent>
            <Typography color="error">{t('credit.show.notFoundDetail')}</Typography>
            <Button variant="contained" onClick={() => navigate('/app/credit')} sx={{ mt: 2 }}>
              {t('credit.show.backToList')}
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const transactions = account.transactions || [];

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <IconButton onClick={() => navigate('/app/credit')}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4">{getCustomerLabel(account.customer)}</Typography>
          <Typography variant="body2" color="text.secondary">
            {account.customer?.email || t('credit.show.noEmail')}
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<PaymentsIcon />}
            aria-expanded={isRecordPaymentOpen}
            onClick={() => setIsRecordPaymentOpen(open => !open)}
          >
            {t('credit.show.recordPayment')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<TuneIcon />}
            aria-expanded={isSetCreditLimitOpen}
            onClick={() => setIsSetCreditLimitOpen(open => !open)}
          >
            {t('credit.show.setCreditLimit')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={isDownloading}
            onClick={handleDownloadStatement}
          >
            {t('credit.show.downloadStatement')}
          </Button>
        </Box>
      </Box>

      {downloadFailed && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDownloadFailed(false)}>
          {t('credit.show.downloadError')}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('credit.balance')}
              </Typography>
              <Typography variant="h4" fontWeight={700}>
                {formatCurrency(account.balance)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('credit.creditLimit')}
              </Typography>
              <Typography variant="h4" fontWeight={700}>
                {formatCurrency(account.creditLimit)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('credit.status.label')}
              </Typography>
              <Chip
                label={getStatusLabel(account.status)}
                color={STATUS_COLORS[account.status] || 'default'}
                variant={account.status === CreditAccountStatus.ACTIVE ? 'filled' : 'outlined'}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('credit.show.ledger')}
              </Typography>
              {transactions.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('credit.show.transactionDate')}</TableCell>
                        <TableCell>{t('credit.show.transactionType')}</TableCell>
                        <TableCell>{t('credit.show.transactionNote')}</TableCell>
                        <TableCell align="right">{t('credit.show.transactionAmount')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map(transaction => (
                        <TableRow key={transaction.id} hover>
                          <TableCell>{formatDateTime(transaction.createdAt)}</TableCell>
                          <TableCell>
                            <Chip
                              label={getTransactionLabel(transaction.type)}
                              size="small"
                              color={TRANSACTION_COLORS[transaction.type] || 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{transaction.note || '—'}</TableCell>
                          <TableCell align="right">{formatCurrency(transaction.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    bgcolor: palette.surfaceElevated,
                    borderRadius: 2,
                  }}
                >
                  <Typography color="text.secondary">{t('credit.show.noTransactions')}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <RecordPaymentModal
        open={isRecordPaymentOpen}
        onClose={() => setIsRecordPaymentOpen(false)}
        accountId={account.id}
        balance={account.balance}
      />

      <SetCreditLimitModal
        open={isSetCreditLimitOpen}
        onClose={() => setIsSetCreditLimitOpen(false)}
        accountId={account.id}
        creditLimit={account.creditLimit}
      />
    </Box>
  );
};

export default CreditShow;
